import { PaymentMethod, useClientApi, useContentResource } from "@ctv/core";
import {
  resolveThousandSeparatorDelimiter,
  Text,
  Token,
  useLocale,
} from "@ctv/shared-core/src";
import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import { useNumericFormat } from "react-number-format";
import {
  BILLING_CYCLES,
  POSITIONS,
  TERMS_OF_PAYMENTS,
} from "../../../_constants";
import { useRegisterContent } from "../../../_contexts/RegisterContentContext";
import {
  VIEW_DOCUMENT_API,
  VIEW_MULTIPLE_DOCUMENT_API,
  ViewDocumentRequest,
  ViewDocumentResponse,
  ViewMultipleDocumentRequest,
  ViewMultipleDocumentResponse,
} from "../../../apis";
import { RegistrationFormValue } from "../../../types";
import { DocumentRow } from "./DocumentRow";
import ReviewCompanyDocument from "./ReviewCompanyDocument";
import ReviewRow from "./ReviewRow";
import ReviewStack from "./ReviewStack";

const paymentMethodToAgreementFormTypeMap = {
  [PaymentMethod.CREDIT_CARD]: "AGREEMENT_FORM_CC",
  [PaymentMethod.INVOICE]: "AGREEMENT_FORM_INVOICE",
};

type Props = {
  agreementFormType: string;
  handleViewDocument(type: string): void;
};

export default function ReviewPage2() {
  const { CorporateRegistration: cr, CorporateRegistrationDropdown: crd } =
    useContentResource();
  const { getValues } = useFormContext<RegistrationFormValue>();
  const { token } = useRegisterContent();
  const {
    paymentMethod,
    agreementDocument,
    ownerName,
    ownerJobTitle,
    serviceFee,
    billingCycle,
    termsOfPayment,
    creditLimit,
    legalNpwpNumber,
    legalSppkpNumber,
    legalName,
    legalFiscalAddress,
    legalFiscalCity,
    legalFiscalPostalCode,
    legalBillingAddress,
    legalBillingCity,
    legalBillingPostalCode,
    partnerCompanyName,
    partnerCompanyRelation,
    partnerCompanyPicName,
    partnerCompanyPicEmail,
    partnerCompanyPhone,
  } = getValues();

  const positionDropdownItems = POSITIONS.map((position) => ({
    label: crd[position],
    value: position,
  }));

  const billingCycles = BILLING_CYCLES.map((cycle) => ({
    label: crd[cycle],
    value: cycle,
  }));

  const termsOfPayments = Object.entries(TERMS_OF_PAYMENTS).map((value) => {
    const [TERMS_OF_PAY, DAY] = value;
    return {
      label: crd[TERMS_OF_PAY],
      value: DAY,
    };
  });

  const locale = useLocale();
  const thousandSeparator = resolveThousandSeparatorDelimiter(locale);

  const thousandFormat = useNumericFormat({
    thousandSeparator: thousandSeparator.thousand,
    decimalSeparator: thousandSeparator.decimal,
  });

  const viewDocument = useClientApi<ViewDocumentResponse, ViewDocumentRequest>({
    domain: "management",
    method: "post",
    path: VIEW_DOCUMENT_API,
  });

  const viewMultipleDocument = useClientApi<
    ViewMultipleDocumentResponse,
    ViewMultipleDocumentRequest
  >({
    domain: "management",
    method: "post",
    path: VIEW_MULTIPLE_DOCUMENT_API,
  });

  async function handleViewDocument(documentType: string, index?: number) {
    let url = "";
    if (index !== undefined) {
      const res = await viewMultipleDocument({
        documentType,
        token,
        index,
      });

      if (res.success) {
        url = res.data.url;
      }
    } else {
      const res = await viewDocument({ documentType, token });

      if (res.success) {
        url = res.data.url;
      }
    }

    window.open(url, "_blank")?.focus();
  }

  return (
    <Fragment>
      <Text variant="title-1" style={styles.section}>
        {cr.directorAttorneyTitle}
      </Text>
      <ReviewStack>
        <ReviewRow label={cr.directorAttorneyFullNameLabel} value={ownerName} />
        <ReviewRow
          label={cr.directorAttorneyPositionLabel}
          value={
            positionDropdownItems.find((i) => i.value === ownerJobTitle)?.label
          }
        />
      </ReviewStack>
      {paymentMethod === PaymentMethod.CREDIT_CARD && (
        <Fragment>
          <Text variant="title-1" style={styles.section}>
            {cr.agreementFormTitle}
          </Text>
          <ReviewStack>
            <DocumentRow
              document={agreementDocument!["AGREEMENT_FORM_CC"]}
              label={cr.agreementFormSignedLabel}
              handleViewDocument={() => handleViewDocument("AGREEMENT_FORM_CC")}
            />
          </ReviewStack>
          <ReviewCompanyDocument
            type="documents"
            style={styles.section}
            handleViewDocument={handleViewDocument}
          />
        </Fragment>
      )}
      {paymentMethod === PaymentMethod.INVOICE && (
        <Fragment>
          <Text variant="title-1" style={styles.section}>
            {cr.paymentProposalTitle}
          </Text>
          <ReviewStack>
            <ReviewRow
              label={cr.paymentProposalServiceFeeLabel}
              value={serviceFee + "%"}
            />
            <ReviewRow
              label={cr.paymentProposalBillingCycleLabel}
              value={billingCycles.find((b) => b.value === billingCycle)?.label}
            />
            <ReviewRow
              label={cr.paymentProposalTermsOfPaymentLabel}
              value={
                termsOfPayments.find((t) => t.value === termsOfPayment)?.label
              }
            />
            <ReviewRow
              label={cr.paymentProposalCredtLimitLabel}
              value={thousandFormat.format!(creditLimit)}
            />
          </ReviewStack>
          <Text variant="title-1" style={styles.section}>
            {cr.legalDetailsTitle}
          </Text>
          <ReviewStack>
            <ReviewRow
              label={cr.legalDetailsNpwpNumberLabel}
              value={legalNpwpNumber}
            />
            <ReviewRow
              label={cr.legalDetailsSppkpNumberLabel}
              value={legalSppkpNumber}
            />
            <ReviewRow
              label={cr.legalDetailsCorporateNameLabel}
              value={legalName}
            />
            <ReviewRow
              label={cr.legalDetailsBusinessAddressLabel}
              value={`${legalFiscalAddress}\n${legalFiscalCity}, ${legalFiscalPostalCode}`}
            />
            <ReviewRow
              label={cr.legalDetailsBillingAddressLabel}
              value={`${legalBillingAddress}\n${legalBillingCity}, ${legalBillingPostalCode}`}
            />
          </ReviewStack>
          <ReviewCompanyDocument
            style={styles.section}
            type="documents"
            handleViewDocument={handleViewDocument}
          />
          <ReviewCompanyDocument
            style={styles.section}
            type="creditAssessmentDocuments"
            handleViewDocument={handleViewDocument}
          />
          <Text variant="title-1" style={styles.section}>
            {cr.referenceCheckSection}
          </Text>
          <ReviewStack>
            <ReviewRow
              label={cr.partnerCompanyNameLabel}
              value={partnerCompanyName}
            />
            <ReviewRow
              label={cr.partnerCompanyRelationLabel}
              value={partnerCompanyRelation}
            />
            <ReviewRow
              label={cr.partnerCompanyPicNameLabel}
              value={partnerCompanyPicName}
            />
            <ReviewRow
              label={cr.partnerCompanyPicEmailLabel}
              value={partnerCompanyPicEmail}
            />
            <ReviewRow
              label={cr.partnerCompanyPhoneLabel}
              value={
                partnerCompanyPhone.phone
                  ? partnerCompanyPhone.prefix + partnerCompanyPhone.phone
                  : "-"
              }
            />
          </ReviewStack>
          <Text variant="title-1" style={styles.section}>
            {cr.signedRegistrationForm}
          </Text>
          <ReviewStack>
            <DocumentRow
              document={agreementDocument!["AGREEMENT_FORM_INVOICE"]}
              label={cr.signedRegistrationFormFieldLabel}
              handleViewDocument={() =>
                handleViewDocument("AGREEMENT_FORM_INVOICE")
              }
            />
          </ReviewStack>
        </Fragment>
      )}
    </Fragment>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: Token.spacing.xl,
    marginBottom: Token.spacing.m,
  },
});
